"use client";

import { useForm } from "react-hook-form";
import { Suspense, useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Eye, EyeOff, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import Link from "next/link";
import Image from "next/image";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { fetchRegister } from "@/lib/services/authService";
import { signIn } from "next-auth/react";
import { handleError, handleNotification } from "@/lib/utils/auxiliars";

/* ── Brand panel (left side, desktop) ────────────────────── */
function BrandPanel({ hasInvitation }) {
  return (
    <div className="relative hidden lg:flex lg:flex-col lg:w-[40%] xl:w-[38%] min-h-screen overflow-hidden bg-[#050d1a]">
      {/* Gradient blobs */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#2563eb] opacity-[0.13] blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#1d4ed8] opacity-[0.10] blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-[#3b82f6] opacity-[0.07] blur-[80px]" />
      </div>
      {/* Subtle grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(248,250,252,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(248,250,252,.6) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* Large semi-transparent watermark logo */}
      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          backgroundImage: "url('/landing/orkestria-full-cream.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center 58%",
          backgroundSize: "min(70vh, 640px) auto",
          opacity: 0.055,
          maskImage: "radial-gradient(closest-side, black 40%, transparent 88%)",
          WebkitMaskImage: "radial-gradient(closest-side, black 40%, transparent 88%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-12 py-10">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#2563eb]/20 border border-[#2563eb]/30 flex items-center justify-center">
            <Image
              src="/landing/orkestria-mark-light.png"
              alt="Orkestria"
              width={18}
              height={18}
              className="object-contain"
            />
          </div>
          <span className="text-[#f8fafc] font-semibold text-[15px] tracking-tight">
            Orkestria
          </span>
        </div>

        {/* Center content */}
        <div className="flex-1 flex flex-col justify-center">
          {hasInvitation ? (
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#10b981]/30 bg-[#10b981]/10 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#34d399]" />
                <span className="text-[11px] font-medium text-[#6ee7b7] tracking-wide uppercase">
                  Invitación recibida
                </span>
              </div>
              <h1 className="text-[#f8fafc] font-semibold text-[34px] xl:text-[38px] leading-[1.05] tracking-tight mb-4">
                Acepta tu<br />
                <span className="text-[#60a5fa]">invitación</span>.
              </h1>
              <p className="text-[#94a3b8] text-[15px] leading-relaxed max-w-[320px]">
                Crea tu cuenta para unirte al restaurante que te invitó y empezar a operar.
              </p>
            </div>
          ) : (
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2563eb]/30 bg-[#2563eb]/10 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#60a5fa] animate-pulse" />
                <span className="text-[11px] font-medium text-[#93c5fd] tracking-wide uppercase">
                  Acceso anticipado
                </span>
              </div>
              <h1 className="text-[#f8fafc] font-semibold text-[34px] xl:text-[38px] leading-[1.05] tracking-tight mb-4">
                Empieza a<br />
                <span className="text-[#60a5fa]">orquestar</span>.
              </h1>
              <p className="text-[#94a3b8] text-[15px] leading-relaxed max-w-[320px]">
                Crea tu cuenta y configura tu restaurante en minutos. Sin compromisos.
              </p>
            </div>
          )}

          {/* Steps */}
          <div className="space-y-4">
            {[
              { n: "01", t: "Crea tu cuenta", d: "Nombre, correo y contraseña." },
              { n: "02", t: "Configura tu restaurante", d: "Nombre, logo y moneda." },
              { n: "03", t: "Empieza a operar", d: "Pedidos, caja y analítica listos." },
            ].map((s) => (
              <div key={s.n} className="flex items-start gap-3">
                <span className="text-[11px] font-mono text-[#475569] mt-px w-7 flex-none">{s.n}</span>
                <div>
                  <p className="text-[#cbd5e1] text-[13.5px] font-medium leading-none mb-0.5">{s.t}</p>
                  <p className="text-[#64748b] text-[12px]">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <p className="text-[#475569] text-[11px]">
          © 2026 Orkestria · <a href="/" className="hover:text-[#94a3b8] transition-colors">criskop.com</a>
        </p>
      </div>
    </div>
  );
}

/* ── Register form ──────────────────────────────────────────── */
function RegisterPageContent() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [notice, setNotice] = useState(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const invitationToken = searchParams.get("invitationToken");

  const loginHref = invitationToken
    ? `/auth/login?invitationToken=${invitationToken}`
    : "/auth/login";

  const values = watch();
  useEffect(() => {
    if (notice) setNotice(null);
  }, [
    values?.email,
    values?.firstName,
    values?.lastName,
    values?.phone,
    values?.password,
    values?.confirmPassword,
  ]);

  const onSubmit = handleSubmit(async (data) => {
    setNotice(null);
    setLoading(true);

    const email = String(data.email).trim();
    const firstName = String(data.firstName || "").trim();
    const lastName = String(data.lastName || "").trim();
    const phone = String(data.phone || "").trim() || undefined;
    const password = String(data.password ?? "");
    const confirmPassword = String(data.confirmPassword ?? "");

    if (password !== confirmPassword) {
      setNotice({ type: "error", text: "Las contraseñas no coinciden." });
      setLoading(false);
      return;
    }

    try {
      const res = await fetchRegister(
        { email, firstName, lastName, phone, password },
        invitationToken,
      );

      if (!res) {
        const msg = "No se pudo registrar. Intenta nuevamente.";
        setNotice({ type: "error", text: msg });
        handleNotification("error", "Error de registro", msg);
        setLoading(false);
        return;
      }

      const loginRes = await signIn("credentials", {
        redirect: false,
        email,
        password,
        callbackUrl: invitationToken
          ? `/dashboard/restaurantes/${res.invitationResult?.restaurantId || ""}`
          : "/dashboard",
      });

      if (loginRes?.ok) {
        router.push(
          invitationToken
            ? `/dashboard/restaurantes/${res.invitationResult?.restaurantId || ""}`
            : "/dashboard",
        );
      } else {
        setLoading(false);
        setNotice({
          type: "success",
          text: "Registro exitoso. Inicia sesión con tus credenciales.",
        });
        handleNotification("success", "Registro exitoso", "Inicia sesión con tus credenciales.");
      }
    } catch (err) {
      handleError(
        err,
        "No se pudo registrar. Intenta nuevamente.",
        undefined,
        setLoading,
        setNotice,
      );
    }
  });

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 sm:px-10 min-h-screen lg:min-h-0 bg-background">
      <div className="w-full max-w-[440px]">
        {/* Mobile logo */}
        <div className="flex items-center gap-2.5 mb-7 lg:hidden">
          <div className="w-7 h-7 rounded-md bg-primary/15 border border-primary/25 flex items-center justify-center">
            <Image
              src="/landing/orkestria-mark-light.png"
              alt="Orkestria"
              width={14}
              height={14}
              className="object-contain opacity-90 dark:opacity-100"
            />
          </div>
          <span className="font-semibold text-[14px] text-foreground tracking-tight">
            Orkestria
          </span>
        </div>

        {/* Heading */}
        <div className="mb-6">
          <h1 className="text-[22px] font-semibold tracking-tight text-foreground mb-1.5">
            {invitationToken ? "Acepta tu invitación" : "Crea tu cuenta"}
          </h1>
          <p className="text-[13.5px] text-muted-foreground">
            {invitationToken
              ? "Regístrate para unirte al restaurante."
              : "Completa el formulario para comenzar."}
          </p>
        </div>

        {/* Invitation badge */}
        {invitationToken && (
          <div className="flex items-center gap-2 p-3 rounded-[9px] bg-primary/8 border border-primary/20 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary flex-none" />
            <span className="text-[12.5px] text-foreground/80">
              Tienes una invitación pendiente. Al registrarte se aplicará automáticamente.
            </span>
          </div>
        )}

        {/* Error notice */}
        {notice && (
          <div
            className={`flex items-start gap-2.5 p-3 rounded-[9px] text-[13px] mb-5 ${
              notice.type === "error"
                ? "bg-destructive/10 text-destructive border border-destructive/20"
                : "bg-primary/10 text-primary border border-primary/20"
            }`}
          >
            <AlertCircle className="w-4 h-4 mt-px flex-none" />
            <span>{notice.text}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Name row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="firstName" className="text-[12.5px] font-medium text-foreground/80">
                Nombre
              </Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Juan"
                autoComplete="given-name"
                className="h-10 text-[13.5px]"
                {...register("firstName", { required: "Requerido" })}
              />
              {errors.firstName && (
                <span className="text-[11.5px] text-destructive">{errors.firstName.message}</span>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName" className="text-[12.5px] font-medium text-foreground/80">
                Apellido
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Pérez"
                autoComplete="family-name"
                className="h-10 text-[13.5px]"
                {...register("lastName", { required: "Requerido" })}
              />
              {errors.lastName && (
                <span className="text-[11.5px] text-destructive">{errors.lastName.message}</span>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[12.5px] font-medium text-foreground/80">
              Correo electrónico
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="tucorreo@ejemplo.com"
              autoComplete="email"
              className="h-10 text-[13.5px]"
              {...register("email", { required: "El correo es requerido" })}
            />
            {errors.email && (
              <span className="text-[12px] text-destructive">{errors.email.message}</span>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone" className="text-[12.5px] font-medium text-foreground/80">
              Teléfono <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <PhoneInput
              id="phone"
              placeholder="+57 300 123 4567"
              autoComplete="tel"
              defaultCountry="CO"
              international
              withCountryCallingCode
              className="h-10 text-[13.5px]"
              {...register("phone")}
            />
          </div>

          {/* Password row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[12.5px] font-medium text-foreground/80">
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className="h-10 text-[13.5px] pr-9"
                  {...register("password", { required: "Requerida" })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full w-9 hover:bg-transparent cursor-pointer"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Ocultar" : "Mostrar"}
                >
                  {showPwd ? (
                    <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.password && (
                <span className="text-[11.5px] text-destructive">{errors.password.message}</span>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-[12.5px] font-medium text-foreground/80">
                Confirmar
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPwd2 ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className="h-10 text-[13.5px] pr-9"
                  {...register("confirmPassword", { required: "Requerida" })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full w-9 hover:bg-transparent cursor-pointer"
                  onClick={() => setShowPwd2((v) => !v)}
                  aria-label={showPwd2 ? "Ocultar" : "Mostrar"}
                >
                  {showPwd2 ? (
                    <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.confirmPassword && (
                <span className="text-[11.5px] text-destructive">{errors.confirmPassword.message}</span>
              )}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-10 text-[13.5px] font-medium cursor-pointer"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                {invitationToken ? "Crear cuenta y aceptar invitación" : "Crear cuenta"}
                <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border/60" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-background px-3 text-[11px] uppercase tracking-widest text-muted-foreground/60">
              O
            </span>
          </div>
        </div>

        {/* Login link */}
        <p className="text-center text-[13px] text-muted-foreground">
          ¿Ya tienes cuenta?{" "}
          <Link
            href={loginHref}
            className="text-primary font-medium hover:underline underline-offset-2 transition-colors"
          >
            Inicia sesión
          </Link>
        </p>

        {/* Footer */}
        <p className="text-center text-[11px] text-muted-foreground/50 mt-7">
          © {new Date().getFullYear()} Orkestria
        </p>
      </div>
    </div>
  );
}

export default function RegisterPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen w-full flex items-center justify-center bg-background">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      }
    >
      <RegisterPageInner />
    </Suspense>
  );
}

function RegisterPageInner() {
  const searchParams = useSearchParams();
  const invitationToken = searchParams.get("invitationToken");
  return (
    <div className="flex min-h-screen w-full bg-background">
      <BrandPanel hasInvitation={!!invitationToken} />
      <RegisterPageContent />
    </div>
  );
}
